import Languages from '../types/enums/languages';
import TranslationManager from '../assets/locale/translationsManager';
import { IMediaState } from '../types/interfaces/states';
import { IPlayerError } from '../types/interfaces/error';
import { IRange, ITrack, ITracks } from '../types/interfaces/player';
import { PlayerState, TimeShift } from '../types/enums/player';

const MILLISECONDS_TO_SECONDS = 1000; // 1000 milliseconds
const MINIMUM_BUFFER_OFFSET = 5; // 5 seconds
const MINIMUM_LIVE_END_OFFSET = 90; // 90 seconds
const MINIMUM_SEEK_SPEED = 1; // 1 second
const MINIMUM_START_OFFSET = 1; // 1 second
const MAXIMUM_END_OFFSET = 10; // 10 seconds
const MAXIMUM_LIVE_END_OFFSET = 300; // 5 minutes
const MAXIMUM_TIMESHIFT_SPEED = 64; // Maximum of +/-64 seconds timeshift speed
const MINIMUM_TIMESHIFT_SPEED = 4; // Minimum of +/-4 seconds timeshift speed

const MediaUtils = {
  clearError(state: IMediaState) {
    if (state.error.retry) {
      state.error = {
        code: undefined,
        player: undefined,
        retry: 0,
        retryPosition: 0,
        type: undefined,
      };
      state.state = PlayerState.IDLE;
    }
  },
  clearRetryPosition(state: IMediaState) {
    state.error.retryPosition = 0;
  },
  clearSeek(state: IMediaState) {
    state.speed = 1;
    state.state = PlayerState.PLAYING;
  },
  reset(state: IMediaState) {
    state.audio = { language: '', id: '' };
    state.bitrate = 0;
    state.currentTime = 0;
    state.duration = 0;
    state.error = {
      code: undefined,
      player: undefined,
      retry: 0,
      type: undefined,
    };
    state.live = false;
    state.position = 0;
    state.ready = false;
    state.speed = 0;
    state.state = PlayerState.IDLE;
    state.startover = false;
    state.subtitles = { language: '', id: '' };
    state.timeshift = false;
    state.tracks = { audio: [], subtitles: [] };
    state.userInteraction = false;
    state.vod = false;
    delete state.range.bookmarks;
    delete state.range.event;
    delete state.range.seek;
  },
  retry(state: IMediaState) {
    state.error = {
      code: undefined,
      player: undefined,
      retry: (state.error.retry || 0) + 1,
      retryPosition: state.position,
      type: undefined,
    };
    state.state = PlayerState.IDLE;
  },
  setAudio(state: IMediaState, audio: ITrack) {
    state.audio = audio;
  },
  setBitrate(state: IMediaState, bitrate: number) {
    state.bitrate = bitrate;
  },
  setCurrentTime(state: IMediaState, currentTime: number) {
    state.currentTime = currentTime;
  },
  setDuration(state: IMediaState, duration?: number) {
    state.duration = duration ? Math.trunc(duration) : 0;
  },
  setError(state: IMediaState, error: IPlayerError) {
    state.error = { ...state.error, ...error };
    this.setState(state, PlayerState.ERROR);
  },
  setLive(state: IMediaState) {
    state.live = true;
  },
  setVod(state: IMediaState) {
    state.vod = true;
  },
  /* Use the following implementation when we have +-10 seconds functionality:
    setPosition(state: IMediaState, position?: number, shouldSeek?: boolean) {
        // Calculate new position
        const bookmarks = state.range.bookmarks;
        const event = state.range.event;
        const seek = state.range.seek;
        let newPosition =
          state.state !== PlayerState.SEEKING
            ? Math.trunc(position ?? state.position)
            : state.position +
              state.speed *
                (state.live ? MILLISECONDS_TO_SECONDS : MINIMUM_SEEK_SPEED);

        // If position received from player is wrong, set a valid one (live)
        if (
          event &&
          seek &&
          shouldSeek === false &&
          state.live === true &&
          state.state !== PlayerState.SEEKING &&
          newPosition < event.start
        ) {
          state.position = seek.end - MINIMUM_BUFFER_OFFSET;
          if (!state.startover) {
            state.timeshift = false;
          }
        }

        // If timeshift is available, limit based on event range (live)
        if (state.timeshift) {
          const now = Date.now();
          if (event && seek) {
            if (newPosition < event.start) {
              const eventOffset = seek.end - event.start;
              newPosition =
                seek.end -
                eventOffset +
                MINIMUM_BUFFER_OFFSET * MILLISECONDS_TO_SECONDS;
              shouldSeek = true;
            }
            if (newPosition > now) {
              newPosition =
                seek.end - MINIMUM_BUFFER_OFFSET * MILLISECONDS_TO_SECONDS;
              shouldSeek = true;
            }
            // Reset startover if on live position
            if (
              state.startover &&
              state.state === PlayerState.PLAYING &&
              newPosition >
                seek.end - MAXIMUM_LIVE_END_OFFSET * MILLISECONDS_TO_SECONDS
            ) {
              state.startover = false;
            }
          }
        }

        // If seek is available, limit based on its range (live & vod)
        if (seek) {
          const start = bookmarks?.start || seek.start;
          if (newPosition < start) {
            newPosition = start + MINIMUM_START_OFFSET;
            shouldSeek = true;
          }
          const end = bookmarks?.end || seek.end;
          if (state.vod && newPosition > end) {
            newPosition = end - MAXIMUM_END_OFFSET;
            shouldSeek = true;
          }
        }

        // Set calculated position
        state.position = newPosition;

        // Seek if required
        shouldSeek && this.setState(state, PlayerState.SEEK);
      },
  */
  setPosition(state: IMediaState, position?: number, shouldSeek?: boolean) {
    // Calculate new position
    const bookmarks = state.range.bookmarks;
    const event = state.range.event;
    const seek = state.range.seek;
    let newPosition =
      state.state !== PlayerState.SEEKING
        ? Math.trunc(position ?? state.position)
        : state.position +
          state.speed *
            (state.live ? MILLISECONDS_TO_SECONDS : MINIMUM_SEEK_SPEED);

    // If position received from player is wrong, set a valid one (live)
    if (
      event &&
      seek &&
      shouldSeek === false &&
      state.live === true &&
      state.state !== PlayerState.SEEKING &&
      newPosition < event.start
    ) {
      newPosition = seek.end - MINIMUM_BUFFER_OFFSET;
      if (!state.startover) {
        state.timeshift = false;
      }
    }

    // If timeshift is available, limit based on event range (live)
    const now = Date.now();
    if (state.timeshift) {
      if (event && seek) {
        if (newPosition < event.start) {
          const eventOffset = seek.end - event.start;
          newPosition =
            seek.end -
            eventOffset +
            MINIMUM_BUFFER_OFFSET * MILLISECONDS_TO_SECONDS;
          shouldSeek = true;
        }
        if (newPosition > now) {
          newPosition =
            seek.end - MINIMUM_BUFFER_OFFSET * MILLISECONDS_TO_SECONDS;
          shouldSeek = true;
        }
        // Reset startover if on live position
        if (
          state.startover &&
          state.state === PlayerState.PLAYING &&
          newPosition >
            seek.end - MAXIMUM_LIVE_END_OFFSET * MILLISECONDS_TO_SECONDS
        ) {
          state.startover = false;
        }
      }
    } else if (!state.timeshift && state.live) {
      if (
        newPosition <
        now - MINIMUM_LIVE_END_OFFSET * MILLISECONDS_TO_SECONDS
      ) {
        newPosition = now;
        shouldSeek = true;
      }
    }

    // If seek is available, limit based on its range (live & vod)
    if (seek) {
      const start = bookmarks?.start || seek.start;
      if (newPosition < start) {
        newPosition = start + MINIMUM_START_OFFSET;
        shouldSeek = true;
      }
      const end = bookmarks?.end || seek.end;
      if (state.vod && newPosition > end) {
        newPosition = end - MAXIMUM_END_OFFSET;
        shouldSeek = true;
      }
    }

    // Set calculated position
    state.position = newPosition;

    // Seek if required
    shouldSeek && this.setState(state, PlayerState.SEEK);
  },
  setRange(state: IMediaState, range?: IRange) {
    if (range?.bookmarks && state.vod) {
      state.range.bookmarks = range?.bookmarks;
    }
    if (range?.event && state.live) {
      state.range.event = range?.event;
    }
    if (range?.seek) {
      state.range.seek = range.seek;
    }
  },
  setReady(state: IMediaState, ready: boolean) {
    state.ready = ready;
  },
  setState(
    state: IMediaState,
    newState?: PlayerState,
    userInteraction = false
  ) {
    state.userInteraction = userInteraction;
    switch (newState) {
      case PlayerState.SEEKING:
        state.state =
          state.speed === 1 ? PlayerState.SEEK : PlayerState.SEEKING;
        break;
      default:
        if (
          [PlayerState.SEEK, PlayerState.SEEKING].includes(state.state) &&
          ![
            PlayerState.PAUSE,
            PlayerState.PLAY,
            PlayerState.PLAYING,
            PlayerState.SEEK,
            PlayerState.SEEKED,
            PlayerState.FAST_FORWARD,
            PlayerState.REWIND,
          ].includes(newState || state.state)
        ) {
          return;
        }
        state.state = newState || state.state;
        state.speed = 1;
        break;
    }
  },
  setSpeed(state: IMediaState, timeshift?: TimeShift) {
    // Disable seeking when a seek is ongoing
    // or player is on a static status
    // or timeshift is not an available service
    if (
      (state.live && !state.timeshift) ||
      ![
        PlayerState.PAUSE,
        PlayerState.PLAYING,
        PlayerState.SEEKING,
        PlayerState.FAST_FORWARD,
        PlayerState.REWIND,
      ].includes(state.state)
    ) {
      return;
    }

    // Calculate speed depending on direction
    let speed = state.speed;
    switch (timeshift) {
      case TimeShift.BACKWARDS:
        if (speed <= MINIMUM_TIMESHIFT_SPEED && speed > 1) {
          state.speed = 1;
        } else {
          if (speed === 1) {
            this.setState(state, PlayerState.REWIND);
            speed = -2;
          }
          state.speed = Math.max(
            -MAXIMUM_TIMESHIFT_SPEED,
            speed <= 1
              ? -Math.abs(speed * 2)
              : Math.max(MINIMUM_TIMESHIFT_SPEED, speed * 0.5)
          );
        }
        break;
      case TimeShift.FORWARD:
        if (speed >= -MINIMUM_TIMESHIFT_SPEED && speed < 1) {
          state.speed = 1;
        } else {
          if (speed === 1) {
            this.setState(state, PlayerState.FAST_FORWARD);
            speed = 2;
          }
          state.speed = Math.min(
            MAXIMUM_TIMESHIFT_SPEED,
            speed >= 1
              ? Math.abs(speed * 2)
              : Math.min(-MINIMUM_TIMESHIFT_SPEED, speed * 0.5)
          );
        }
        break;
      default:
        state.speed = 1;
        break;
    }
    // Change state to seeking
    this.setState(state, PlayerState.SEEKING);
  },
  setSubtitles(state: IMediaState, subtitles: ITrack) {
    state.subtitles = subtitles;
  },
  setTimeshift(state: IMediaState, timeshift: boolean) {
    state.timeshift = timeshift;
  },
  setTracks(state: IMediaState, tracks: ITracks) {
    // Sort tracks
    const availableLanguages = Object.keys(TranslationManager.values);
    const defaultLanguage = TranslationManager.currentLanguage;
    tracks.audio.sort((item) => {
      if (item.language === defaultLanguage) return -1;
      else if (item.language === Languages.ORIGINAL) return 1;
      else return 0;
    });

    // Add disable button to subtitles and initialize
    const disabledSubtitles = { id: '', language: '' };
    tracks.subtitles.splice(0, 0, disabledSubtitles);
    if (!state.subtitles?.id) {
      state.subtitles = disabledSubtitles;
    }

    // Set tracks and audio
    state.tracks = tracks;
    if (!state.audio.id && tracks.audio.length) {
      state.audio =
        tracks.audio.find((item) => item.language === defaultLanguage) ||
        tracks.audio.find((item) =>
          availableLanguages.includes(item.language)
        ) ||
        tracks.audio.find((item) => item.language === Languages.ORIGINAL) ||
        tracks.audio[0];
    }
  },
  toggle(state: IMediaState) {
    switch (state.state) {
      case PlayerState.PAUSE:
      case PlayerState.SEEK:
      case PlayerState.SEEKED:
      case PlayerState.WAITING:
        this.setState(state, PlayerState.PLAY, true);
        break;
      case PlayerState.PLAY:
      case PlayerState.PLAYING:
        this.setState(state, PlayerState.PAUSE, true);
        break;
      case PlayerState.SEEKING:
        this.setState(state, PlayerState.SEEK, true);
        break;
      default:
        this.setState(state, PlayerState.PLAY, true);
        break;
    }
  },
};

export default MediaUtils;
